/**
 * Null-safe localStorage / sessionStorage access.
 *
 * `window.localStorage` is not universally reachable in a browser:
 *   - Firefox with `dom.storage.enabled=false` exposes it as `null`
 *   - Chrome with all cookies blocked throws SecurityError on access
 *   - Sandboxed iframes and hardened webviews throw on access
 *   - Headless/automated browsers frequently null it out
 *   - Safari private browsing permits access but can throw on write (quota)
 *
 * Reads return null and writes no-op when storage is unavailable. There is
 * deliberately no in-memory fallback: every caller already tolerates "nothing
 * stored" (the magic-link flow falls back to its confirm-email prompt), and a
 * memory shim would not survive the page reload those flows depend on while
 * adding a second set of semantics to reason about.
 *
 * Fixes JAVASCRIPT-REACT-B (2026-08-01): an unguarded `localStorage.getItem`
 * in AuthProvider's mount effect threw on a null store, and because
 * AuthProvider wraps the whole route tree the ErrorBoundary replaced every
 * page with the error fallback.
 */

type StorageKind = 'local' | 'session';

const resolve = (kind: StorageKind): Storage | null => {
    try {
        if (typeof window === 'undefined') return null;
        return (kind === 'local' ? window.localStorage : window.sessionStorage) ?? null;
    } catch {
        // SecurityError — the store exists but access is denied.
        return null;
    }
};

export const safeStorage = {
    getItem(key: string, kind: StorageKind = 'local'): string | null {
        const store = resolve(kind);
        if (!store) return null;
        try {
            return store.getItem(key);
        } catch {
            return null;
        }
    },

    setItem(key: string, value: string, kind: StorageKind = 'local'): void {
        const store = resolve(kind);
        if (!store) return;
        try {
            store.setItem(key, value);
        } catch {
            // QuotaExceededError, or a store that allows reads but not writes.
        }
    },

    removeItem(key: string, kind: StorageKind = 'local'): void {
        const store = resolve(kind);
        if (!store) return;
        try {
            store.removeItem(key);
        } catch {
            // Nothing actionable — the value is already unreachable.
        }
    },
};
